

import Vue from 'vue'
import Component from "vue-class-component";
import {namespace} from "vuex-class";
import {IOnOffHandlers} from "bonomo/dist";
import {OrderHelper} from "@/helpers/OrderHelper";
import {IErrors} from "@/model/IErrors";
import StarRating from "vue-star-rating";
import OOCommunicationMessages from "@/components/order/order/OOCommunicationMessages.vue";
import OOLogs from "@/components/order/order/OOLogs.vue";
import OOImages from "@/components/order/order/OOImages.vue";
import OONotes from "@/components/order/order/OONotes.vue";
import ShortUserCard from "@/components/user/ShortUserCard.vue";
import OOLocation from "@/components/order/order/OOLocation.vue";
import OOProfessionalServicePrice from "@/components/order/order/OOProfessionalServicePrice.vue";
import OOProfessionalServiceDetails from "@/components/order/order/OOProfessionalServiceDetails.vue";
import {EOrderPaymentStatuses, EOrderStatuses, EOrderTypes, IOrder} from "@/model/Order/orderInterfaces";
import {Watch} from "vue-property-decorator";
import {UrlHelper} from "@/helpers/UrlHelper";
import Datim from "@/components/shared/Datim.vue";
import OOServices from "@/components/order/order/OOServices.vue";
import PaymentStatusChip from "@/components/shared/chips/PaymentStatusChip.vue";
import {flattenErrors} from "@/helpers/errorsHelper";
import VueI18n from "vue-i18n";
import {EPermissionActions} from "@/model/EPermissionActions";
import {RawLocation} from "vue-router";
import OrderByHandDialog from '@/components/shared/CollectByAdministratorDialog.vue'
import HelpCard from "@/components/shared/HelpCard.vue";
import OrderStatusChip from "@/components/shared/chips/OrderStatusChip.vue";
import TranslateResult = VueI18n.TranslateResult;
import {IChipStyle} from "@/model/chipStyleInterfaces";
import OrderChip from "@/components/shared/chips/OrderChip.vue";
import OrderStatusStepper from "@/components/order/order/OrderStatusStepper.vue";
import SimpleConfirmationDialog from "@/components/shared/SimpleConfirmationDialog.vue";
import CollectByAdministratorDialog from "@/components/shared/CollectByAdministratorDialog.vue";
import {PermissionHelper} from "@/helpers/PermissionHelper";
import {IAuthUserData} from "@/store/modules/authModule";
import {EUserPermissions} from "@/model/EUserPermissions";

const orderModule = namespace("orderModule");
const authModule = namespace("authModule");

  @Component({
    components: {
      CollectByAdministratorDialog,
      SimpleConfirmationDialog,
      HelpCard,
      ShortUserCard,
      StarRating,
      OOCommunicationMessages,
      OOLogs,
      OONotes,
      OOLocation,
      OOServices,
      OOImages,
      Datim,
      PaymentStatusChip,
      OrderByHandDialog,
      OrderStatusStepper,
      OrderStatusChip,
      OOProfessionalServicePrice,
      OOProfessionalServiceDetails,

    }
  })
  export default class Order extends Vue {

    @orderModule.Getter('order')
    private order!: IOrder;
    @orderModule.Getter('errors')
    private errors!: IErrors;
    @orderModule.Action('callDevEvent')
    private callDevEvent!: (params: {orderId: number, eventName: string}) => any;
    @authModule.Getter('userData')
    protected authUser!: IAuthUserData;

    @Watch('$route')
    private onRouteChanged() {
      this.loadData();
    }

    protected orderEvents: string[] = [
        'ImageAddedToOrderEvent',
        'OrderAssembledEvent',
        'OrderChangedCellEvent',
        'OrderCollectedEvent',
        'OrderCreatedEvent',
        'OrderDeletedEvent',
        'OrderDeliveredEvent',
        'OrderDropoffEvent',
        'OrderFinalizedEvent',
        'OrderLockerCellClaimedEvent',
        'OrderPaidEvent',
        'OrderPickedUpEvent',
        'OrderRatedEvent',
        'OrderServiceProcessedEvent',
        'OrderSilencedEvent',
        'PackageDeliveryCreatedEvent',
        'SpConfirmedOrderEvent',
    ];
    protected showDialog: boolean = false;
    protected dialogText: TranslateResult = {};
    protected confirmWithReason: boolean = false;
    protected confirmReason: string = '';
    protected confirmReasonError: boolean = false;
    protected dialogCallback: () => void = () => undefined;
    protected loading: boolean = true;
    protected submitting: boolean = false;
    private orderId: number = 0;
    private subscribes: IOnOffHandlers = {};
    private showCollectByHandDialog = false;


    protected get showAdminCollectButton(): boolean {
      return this.order.status === 'status_awaiting_customer_dropoff'
          && this.$canAction(EPermissionActions.ACTION_ORDERS_UPDATE);
    }

    // COMPUTED
    protected get canDevEvent(): boolean {
      return (window.location.href.includes('local') || window.location.href.includes('s2')) &&
          this.$canAction(EPermissionActions.ACTION_ORDERS_DEV_EVENT);
    }

    protected get serviceProviderLocation(): RawLocation {
      return { name: 'serviceProvider', params: { spId: '' + this.order.service_provider.id }};
    }

    protected get orderClosed(): boolean {
      return OrderHelper.closedStatuses.includes(this.order.status);
    }
    protected get orderChipStyle(): IChipStyle {
      return OrderChip.chipStyleOf(this.order.type);
    }

    protected get isPackageDelivery(): boolean {
      return this.order.type === EOrderTypes.delivery_with_code;
    }

    protected get canProcess(): boolean {
      return OrderHelper.canProcess(this.order) &&
          this.$canAction(EPermissionActions.ACTION_ORDERS_PROCESS) && false;
    }
    protected get canDeleteOrder(): boolean {
      if (!this.$canAction(EPermissionActions.ACTION_ORDERS_UPDATE)) {
        return false;
      }
      return (this.order.payment_status != EOrderPaymentStatuses.status_payment_paid) ||
          (parseFloat(this.order.grand_total) == 0);
    }
    protected get canBeRepeated(): boolean {
      return this.$canAction('orders/create') &&
          this.order.type !== EOrderTypes.delivery_with_code &&
          this.order.type !== EOrderTypes.drop_only;
    }

    // Visibility
    protected get showFinalizeOrder(): boolean {
      return OrderHelper.canFinalize(this.order);
    }
    protected get showConfirmProfessionalService(): boolean {
      return this.order.type === EOrderTypes.professional_service &&
          this.order.status === EOrderStatuses.status_order_professional_service_search &&
          this.$canAction(EPermissionActions.ACTION_ORDERS_PROCESS);
    }
    protected get isProfessionalService(): boolean {
      return this.order.type === EOrderTypes.professional_service;
    }
    protected get canFinalizeOrder(): boolean {
      return (this.$canAction('orders/update') || this.$canAction('orders/process')) &&
          this.order.status !== EOrderStatuses.status_order_completed &&
          (
              this.order.payment_status === EOrderPaymentStatuses.status_payment_paid ||
              this.order.payment_status === EOrderPaymentStatuses.status_payment_paid_invoice ||
              this.order.payment_status === EOrderPaymentStatuses.status_payment_unpaid_invoice ||
              this.order.type === EOrderTypes.delivery_with_code
          );
    }
    protected get showPay(): boolean {
      return this.$canAction(EPermissionActions.ACTION_ORDERS_PROCESS) &&
          !this.order.deleted_at &&
          this.order.type !== EOrderTypes.delivery_with_code &&
          OrderHelper.canPay(this.order);
    }
    protected get canPay(): boolean {
      return this.order.locked;
    }
    protected get showUnPay(): boolean {
      return this.$canAction(EPermissionActions.ACTION_ORDERS_PROCESS) &&
          !OrderHelper.closedStatuses.includes(this.order.status) &&
          !this.order.deleted_at &&
          this.order.type !== EOrderTypes.delivery_with_code &&
          OrderHelper.isPaid(this.order);
    }
    protected get canUnPay(): boolean {
      return true;
    }
    protected get showRequestPickup(): boolean {
      return this.$canAction(EPermissionActions.ACTION_ORDERS_PROCESS) &&
          !this.order.deleted_at &&
          OrderHelper.canRequestPickup(this.order);
    }

    protected get flatErrors(): string[] {
      return flattenErrors(this.errors);
    }

    protected get showServices(): boolean {
      const ordersWithoutServices = ['delivery_with_code', 'smart_drop', 'package_transfer'];
      return !ordersWithoutServices.includes(this.order.type);
    }
    get hasPermissionToSeeBlock(): boolean {
      const authUser = this.authUser;
      if (!authUser) return false;
      return PermissionHelper.hasPermissionAny(authUser, EUserPermissions.PERMISSION_ADMINISTRATOR)
          || PermissionHelper.hasPermissionAny(authUser, EUserPermissions.PERMISSION_LOCKER_OPERATOR);
    }

    get hasPermissionToSeeComms(): boolean {
      const authUser = this.authUser;
      if (!authUser) return false;
      return PermissionHelper.hasPermissionAny(authUser, EUserPermissions.PERMISSION_ADMINISTRATOR);
    }

    created() {
      this.loadData();
      this.subscribes = (this as any).$ons({
        'login.OK': this.loadData,
        'backend.call.OK.orderLoaded': this.onOrderLoaded,
        'backend.call.KO': this.onOrderLoadedKO,
        'backend.call.OK.orderRepeated': () => { this.submitting = false; },
        'backend.call.KO.orderRepeated': () => { this.submitting = false; },
      });
    }
    beforeDestroy() {
      this.$offs(this.subscribes);
    }

    protected confirmFinalizeOrder() {
      this.dialogText = this.$t('orders.order.CTA.finalize');
      this.dialogCallback = this.onFinalizeOrder;
      this.$store.dispatch('orderModule/errors', []);
      this.confirmWithReason = false;
      this.showDialog = true;
    }
    protected confirmDeleteOrder() {
      this.dialogText = this.$t('orders.order.CTA.delete');
      this.dialogCallback = this.onDeleteOrder;
      this.$store.dispatch('orderModule/errors', []);
      this.confirmWithReason = false;
      this.showDialog = true;
    }
    protected confirmProfessionalServiceConfirmed() {
      this.dialogText = this.$t('orders.order.CTA.confirm_professional_service');
      this.dialogCallback = this.onConfirmProfessionalService;
      this.$store.dispatch('orderModule/errors', []);
      this.confirmWithReason = false;
      this.showDialog = true;
    }
    protected onConfirmProfessionalService() {
      this.$store.dispatch('orderModule/confirmProfessionalService', this.orderId);
    }
    
    protected onFinalizeOrder() {
      this.$store.dispatch('orderModule/finalizeOrder', this.orderId);
    }
    private onDeleteOrder(){
      this.submitting = true;
      this.$store.dispatch('orderModule/deleteOrder', this.orderId);
    }
    protected serviceProcessed(ev: {orderServiceId: number, processed: boolean}) {
      this.submitting = true;
      this.$store.dispatch(
          'orderModule/serviceProcessed',
          {
            orderId: this.order.id,
            orderServiceId: ev.orderServiceId,
            processed: ev.processed,
          });
    }
    protected confirmConfirmOrder() {
      this.dialogText = this.$t('orders.services.CTA.confirm');
      this.dialogCallback = this.onConfirmOrder;
      this.$store.dispatch('orderModule/errors', []);
      this.confirmWithReason = false;
      this.showDialog = true;
    }
    protected onConfirmOrder() {
      this.submitting = true;
      this.$store.dispatch('orderModule/confirmOrder', this.order.id);
    }
    protected onResendConfirmation() {
      this.submitting = true;
      this.$store.dispatch('orderModule/resendConfirmation', this.order.id);
    }

    protected confirmAssembleOrder() {
      this.dialogText = this.$t('orders.services.CTA.assemble');
      this.dialogCallback = this.onAssembleOrder;
      this.$store.dispatch('orderModule/errors', []);
      this.confirmWithReason = false;
      this.showDialog = true;
    }
    protected onAssembleOrder() {
      this.submitting = true;
      this.$store.dispatch('orderModule/assembleOrder', this.order.id);
    }

    protected onPayOrder() {
      this.submitting = true;
      this.$store.dispatch('orderModule/payOrder', this.orderId);
    }
    protected confirmUnPayOrder() {
      this.dialogText = this.$t('orders.order.CTA.unPay');
      this.dialogCallback = this.onUnPayOrder;
      this.$store.dispatch('orderModule/errors', []);
      this.confirmWithReason = false;
      this.showDialog = true;
    }
    protected onUnPayOrder() {
      this.submitting = true;
      this.$store.dispatch('orderModule/unPayOrder', this.orderId);
    }

    protected confirmRequestPickupOrder() {
      this.dialogText = this.$t('orders.order.CTA.requestPickup');
      this.dialogCallback = this.onRequestPickupOrder;
      this.$store.dispatch('orderModule/errors', []);
      this.confirmWithReason = false;
      this.showDialog = true;
    }
    protected onRequestPickupOrder() {
      this.submitting = true;
      this.$store.dispatch('orderModule/requestPickupOrder', this.orderId);
    }

    protected confirmSilenceOrder() {
      this.dialogText = this.$t('orders.order.CTA.silence');
      this.dialogCallback = this.onSilenceOrder;
      this.$store.dispatch('orderModule/errors', []);
      // I should really abstract this next time
      this.confirmWithReason = true;
      this.confirmReason = '';
      this.confirmReasonError = false;
      this.showDialog = true;
    }
    private onSilenceOrder() {
      if (this.confirmReason.length < 10) {
        this.confirmReasonError = true;
        return;
      }
      this.$store.dispatch('orderModule/silenceOrder', {orderId: this.orderId, reason: this.confirmReason});
    }

    protected onGotoOrder() {
      this.$router.push({ name: 'orderEdit', params: { orderId: String(this.order.id) } });
    }

    protected showRepeatDialog: boolean = false;
    protected repeatFormValid: boolean = false;
    protected repeatForm: { first_date: string; frequency: string; cnt: number } = {
      first_date: '',
      frequency: 'week',
      cnt: 1,
    };

    protected get repeatedOrderIds(): number[] {
      return this.$store.getters['orderModule/repeatedOrderIds'] || [];
    }

    protected get repeatFrequencyOptions() {
      return [
        { text: this.$t('orders.order.CTA.repeat.frequencies.week'), value: 'week' },
      ];
    }

    protected get repeatCntOptions() {
      const opts = [];
      for (let i = 1; i <= 15; i++) { opts.push(i); }
      return opts;
    }

    protected onGotoRepeat() {
      this.repeatForm = { first_date: '', frequency: 'week', cnt: 1 };
      this.$store.commit('orderModule/orderRepeated', { order_ids: [] });
      this.showRepeatDialog = true;
    }

    protected closeRepeatDialog() {
      this.showRepeatDialog = false;
    }

    protected onConfirmRepeat() {
      this.submitting = true;
      this.$store.dispatch('orderModule/repeatOrder', {
        orderId: this.orderId,
        first_date: this.repeatForm.first_date,
        frequency: this.repeatForm.frequency,
        cnt: this.repeatForm.cnt,
      });
    }

    protected goToOrder(id: number) {
      this.$router.push({ name: 'order', params: { orderId: String(id) } });
      this.closeRepeatDialog();
    }

    protected printTicket() {
      const route = this.$router.resolve({ name: 'orderPrint', params: { orderId: String(this.orderId) } });
      window.open(route.href, '_blank');
    }

    protected onDevEvent(eventName: string) {
      this.submitting = true;
      this.callDevEvent({orderId: this.orderId, eventName});
    }

    private loadData() {
      this.orderId = parseInt(this.$route.params.orderId);
      this.loading = true;
      this.$store.dispatch('orderModule/fetchOrder', this.orderId);
    }

    private onOrderLoaded() {
      this.loading = false;
      this.submitting = false;
      this.showDialog = false;
    }
    private onOrderLoadedKO() {
      this.submitting = false;
      // this.showDialog = false;
    }

    private onCollectByHand() {
      this.showCollectByHandDialog = true;
    }

    goToOrderPage(orderId: number): string {
      return UrlHelper.mayorOrder(orderId);
    }

  }

